import {addGimmickMatchMatches, GimmickMatch} from "../models/gimmickMatch.js";
import {
    createCompanyAlert,
    createCustomSegmentItem, generateGimmickMatch,
    getCompanyLatestEvent,
    getCompanyRosterByType
} from "./companies.js";
import {getRosterSuperstars} from "../models/roster.js";
import {
    addCompanyCompanyAlerts, addCompanyGimmickMatches,
    addCompanyStoryChoices, getCompanyGame,
    getCompanyObjectives,
    getCompanyStoryChoices
} from "../models/company.js";
import {getGraphicForSuperstar, getRankFromPopularity} from "./superstars.js";
import {getCurrentGame, getRandomInt, getUniqueObjects, myround} from "./helpers.js";
import {getGameCurrentUserCompany} from "./games.js";
import {powDialog} from "../data/dialogTrees.js";
import {addObjectiveProperties, getObjectiveProperties, Objective} from "../models/objective.js";
import {
    GimmickMatches,
    Matches,
    Objectives,
    Properties,
    SegmentItems,
    Segments,
    StoryChoices,
    Superstars
} from "../models/models.js";
import {addPropertySuperstars, getPropertySuperstars, Property} from "../models/property.js";
import {StoryChoice} from "../models/storyChoice.js";
import {addEventMatches, addEventSegments, getEventMatches} from "../models/event.js";
import {addSegmentSuperstars, Segment} from "../models/segment.js";
import {addSegmentItemSegments} from "../models/segmentItem.js";
import {getSuperstarTagTeams, Superstar} from "../models/superstar.js";
import {addMatchFeuds, addMatchParticipants, Match} from "../models/match.js";
import {setManagersForMatchOrSegment} from "./managers.js";
import {getParticipantFeuds} from "./feuds.js";
import {loadUser, saveUser} from "./user.js";

function createFemmeFataleMatch() {  // create_femme_fatale_match
    const newGimmickMatch = GimmickMatch();

    newGimmickMatch.name = 'Femme Fatale';
    newGimmickMatch.description = 'A story-driven, women only match invented by Hank Hughes.';
    newGimmickMatch.cost = 70;
    newGimmickMatch.ratingBoost = 1.25;
    newGimmickMatch.popularityIncrease = 2;
    newGimmickMatch.popularityDecrease = 0.6;
    newGimmickMatch.injuryRate = 1.05;
    newGimmickMatch.special = true;

    return newGimmickMatch;
}

async function getPowDialog() {  // get_pow_dialog
    let data = {};
    const game = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(game);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const goals = await getCompanyObjectives(userCompany);
    const storyChoices = (await getCompanyStoryChoices(userCompany)).reverse();
    const powSuccessEnding = storyChoices.find(x => x.choice === 'success_ending');

    for (let [person, dialog] of Object.entries(powDialog)) {
        // Hank Hughes' dialog if not at success ending
        if (person === 'Hank Hughes' && !powSuccessEnding) {
            let storyPosition;
            if (game.monthCount === 0 && game.year === 0) {
                storyPosition = 'introduction'
            }
            else {
                storyPosition = 'normal'
            }
            data[person] = dialog[storyPosition];
            data[person]['information'] = dialog['information'];
            data[person]['storyPosition'] = storyPosition;

            // Remove 'what do you think of x' dialogs if their goal has finished
            data[person]['choices'] = data[person]['choices'].filter(y => {
                return !y['person_name'] ||
                    goals.find(x => x.name === `Make ${y['person_name']} a Star` && !x.completed && !x.failed)
            });
        }
        // Stars' dialog
        else {
            let superstar = rosterSuperstars.find(x => x.name === person);
            if (superstar && !superstar.dead && !superstar.retired) {
                // Get dialog if available based on story position
                let storyPosition;
                if (game.monthCount === 0 && game.year === 0) {
                    storyPosition = 'introduction'
                }
                else {
                    let storyChoice = storyChoices.find(x => x.information && x.information === superstar.name);
                    if (storyChoice) {
                        storyPosition = storyChoice.choice
                    }
                    else {
                        if (checkPowStarRequirement(superstar)) {
                            storyPosition = 'confident'
                        }
                        else {
                            storyPosition = 'normal'
                        }
                    }
                }

                if (dialog[storyPosition]) {
                    data[person] = dialog[storyPosition];
                    data[person]['information'] = getPowDialogPersonInformation(superstar)
                }
            }
        }
    }

    return data;
}

function getPowDialogPersonInformation(superstar) {  // get_pow_dialog_person_information
    let graphic = getGraphicForSuperstar(superstar);
    delete graphic.uid;
    let workrate = superstar.hot ? superstar.workrate + 1 : superstar.workrate;
    let charisma = superstar.hot ? superstar.charisma + 1 : superstar.charisma;
    return {
        'popularity': superstar.popularity, 'hot': superstar.hot, 'name': superstar.name, 'workrate': workrate,
        'charisma': charisma, 'overness': superstar.overness, 'graphic': graphic
    }
}

function checkPowStarRequirement(superstar) {  // check_pow_star_requirement
    // Check if star's requirement has been fulfilled

    if (superstar.name === 'Lady Julia') {
        return true;
    }

    if (superstar.name === 'Violet') {
        if (superstar.workrate >= 3) {
            return true;
        }
    }

    if (superstar.name === 'Natasha Ali') {
        if (getRankFromPopularity(superstar.popularity) === 'Main Eventer') {
            return true;
        }
    }

    if (superstar.name === 'Zora') {
        if (superstar.hot) {
            return true;
        }
    }
}

async function createPowObjectives(company) {
    let objectives = [];

    // Sponsors goal
    let mainObjective = Objective('Get some Sponsors');
    Objectives.insert(mainObjective);
    let mainObjectiveProperty = Property('get sponsors', 1, 'company', null);
    Properties.insert(mainObjectiveProperty);
    await addObjectiveProperties(mainObjective, [mainObjectiveProperty]);
    objectives.push(mainObjective);

    // Stars' goals
    let mainRoster = await getCompanyRosterByType(company, 'main');
    let superstars = (await getRosterSuperstars(mainRoster))
        .filter(x => ['Lady Julia', 'Violet', 'Natasha Ali', 'Zora'].includes(x.name));
    for (let superstar of superstars) {
        let starObjective = Objective(`Make ${superstar.name} a Star`);
        Objectives.insert(starObjective);
        let starObjectiveProperty = Property('star', 1, 'superstar', null, superstar.name);
        Properties.insert(starObjectiveProperty);
        await addObjectiveProperties(starObjective, [starObjectiveProperty]);
        await addPropertySuperstars(starObjectiveProperty, [superstar]);
        objectives.push(starObjective);
        if (superstar.name === 'Violet') {
            superstar.workrateXp = 150
        }
        if (superstar.name === 'Zora') {
            superstar.morale = 30
        }
    }

    return objectives;
}

async function processPowStarResult(superstar, company) {
    let data = {};
    let goals = await getCompanyObjectives(company);
    let goal = goals.find(x => x.name === `Make ${superstar.name} a Star` && !x.completed && !x.failed);

    if (goal) {
        let goalProperty = (await getObjectiveProperties(goal))[0];
        let goalSuperstar = (await getPropertySuperstars(goalProperty))[0];
        if (goalSuperstar.$loki === superstar.$loki) {
            let isStar = checkPowStarRequirement(superstar);
            if (isStar) {
                goalProperty.value += 1;
                goal.completed = true;
                data['completed'] = true;
                let newCompanyAlert = await createCompanyAlert('goalCompleted', goal.name);
                await addCompanyCompanyAlerts(company, [newCompanyAlert]);
            }
            else {
                goal.failed = true;
                data['failed'] = true;
                let newCompanyAlert = await createCompanyAlert('goalFailed', goal.name);
                await addCompanyCompanyAlerts(company, [newCompanyAlert]);
            }

            // Add star changes
            data['superstarChanges'] = await addPowStarResultChanges(superstar, isStar);

            // Add story choice
            let storyWord = isStar ? 'success' : 'failure';
            let storyChoice = createPowStoryChoice(`${storyWord}_post_event`, superstar.name);
            await addCompanyStoryChoices(company, [storyChoice]);
        }
    }

    return data;
}

function createPowStoryChoice(choice, information = null) {  // add_pow_story_choice
    let storyChoice = StoryChoice();
    storyChoice.choice = choice;
    StoryChoices.insert(storyChoice);
    if (information) {
        storyChoice.information = information
    }
    return storyChoice;
}

async function addPowStarResultChanges(superstar, isStar) {
    let data = {};

    // Add success effect
    if (isStar) {
        if (superstar.name === 'Lady Julia') {
            superstar.workrateXp = 80;
            superstar.workrate = 1;
        }
        if (superstar.overness < 80) {
            superstar.overness += 20;
            superstar.stale = false;
        }
        if (superstar.morale < 40) {
            superstar.morale += 20;
        }
    }
    // Add failure effect
    else {
        if (superstar.name === 'Violet') {
            superstar.partTimer = true;
            superstar.appearances = getRandomInt(2, 6);
        }
        if (superstar.name === 'Natasha Ali') {
            data['popularityChange'] = -150;
            superstar.popularity += data['popularityChange'];
            if (superstar.popularity < 0) {
                superstar.popularity = 0
            }
        }
        if (superstar.name === 'Zora') {
            superstar.overness = 5;
            superstar.stale = true;
        }
        if (superstar.morale >= 60) {
            if (superstar.morale >= 80) {
                superstar.morale += -45;
            }
            else {
                superstar.morale += -25
            }
        }
    }

    return data;
}

function getPowPostEventDialogs(storyChoices, rosterSuperstars) {
    // Get post event dialog after 'post_event' if 'next_event' not added
    let dialogs = [];

    for (let storyChoice of storyChoices) {
        let {storyPosition} = getPowStoryPositionAndResult(storyChoice);
        if (storyPosition === 'post_event') {
            let personName = storyChoice.information;
            let nextStoryPosition = getPowNextStoryPosition(storyPosition);
            let nextStoryChoice = storyChoices.find(x => x.information === personName &&
                x.choice.split('_').slice(1).join('_') === nextStoryPosition);
            if (!nextStoryChoice) {
                // Get dialog and information
                let dialogData = powDialog[personName][storyChoice.choice];
                let superstar = rosterSuperstars.find(x => x.name === personName);
                dialogData['information'] = getPowDialogPersonInformation(superstar);
                dialogs.push(dialogData);
            }
        }
    }

    return dialogs;
}

function getPowStoryPositionAndResult(storyChoice) {
    let storyChoiceSplit = storyChoice.choice.split('_');
    return {storyPosition: storyChoiceSplit.slice(1).join('_'), resultWord: storyChoiceSplit[0]};
}

function getPowNextStoryPosition(storyPosition) {
    if (storyPosition === 'post_event') {
        return 'next_event'
    }
    if (storyPosition === 'next_event') {
        return 'normal'
    }
    if (storyPosition === 'goals') {
        return 'pre_premier_event'
    }
    if (storyPosition === 'pre_premier_event') {
        return 'ending'
    }
    if (storyPosition === 'ending') {
        return 'post_ending'
    }
}

async function processPowStoryChoices(storyChoices, company, event) {
    // Runs after monthly event, adds next story choice
    const objectives = await getCompanyObjectives(company);
    const sponsorGoal = objectives.find(x => x.name === 'Get some Sponsors');
    const starGoals = objectives.filter(x => x.$loki !== sponsorGoal.$loki);

    for (let storyChoice of storyChoices) {

        // Get result and story position
        let {storyPosition, resultWord} = getPowStoryPositionAndResult(storyChoice);
        let nextStoryPosition = getPowNextStoryPosition(storyPosition);

        // If at 'pre_premier_event' in story and premier event not held
        if (storyPosition === 'pre_premier_event' && !sponsorGoal.completed && !sponsorGoal.failed) {

            // Check for premier event result if at monthly event, complete/fail sponsors goal
            if (!event.weeklyShow && event.name !== 'Weekly Show') {
                resultWord = getPowPremierEventResult(starGoals.filter(x => x.completed).length, event.rating);
                if (resultWord === 'success') {
                    sponsorGoal.completed = true;
                    await processPowPremierEventSuccess(company);
                }
                else {
                    sponsorGoal.failed = true;
                }
            }
            else {
                nextStoryPosition = null;  // Skip next story position if not at monthly event
            }
        }

        // If at 'goals' in story and premier event not held
        if (storyPosition === 'goals' && !sponsorGoal.completed && !sponsorGoal.failed) {
            // Process premier event if next event is monthly event
            let latestEvent = await getCompanyLatestEvent(company);
            if (!latestEvent.weeklyShow && latestEvent.name !== 'Weekly Show') {
                latestEvent.activeEffect = 'premier_event';
                await processPowPremierEvent(company, latestEvent, resultWord);
            }
        }

        // Add next story position
        if (nextStoryPosition) {
            let nextStoryChoiceText = `${resultWord}_${nextStoryPosition}`;
            await checkToAddPowStoryChoice(storyChoices, company, nextStoryChoiceText, storyChoice.information);
        }
    }

    // Add goals story choice after goals finished
    if (starGoals.every(x => x.completed || x.failed)) {
        let resultWord;
        if (starGoals.every(x => x.completed)) {
            resultWord = 'success'
        }
        else {
            resultWord = 'failure'
        }
        let nextStoryChoiceText = `${resultWord}_goals`;
        await checkToAddPowStoryChoice(storyChoices, company, nextStoryChoiceText);
    }
}

async function checkToAddPowStoryChoice(storyChoices, company, storyChoiceText, information = null) {
    let nextStoryPosition = storyChoiceText.split('_').slice(1).join('_');
    let nextStoryChoice = storyChoices.find(x => x.information === information &&
        x.choice.split('_').slice(1).join('_') === nextStoryPosition);
    if (!nextStoryChoice) {
        let newStoryChoice = createPowStoryChoice(storyChoiceText, information);
        await addCompanyStoryChoices(company, [newStoryChoice]);
    }
}

async function getPowProducerReactionDialogs(company) {
    let dialogs = [];
    let storyChoices = (await getCompanyStoryChoices(company)).reverse();

    // Get next event and ending dialogs
    let nextEventDialogs = getPowProducerNextEventDialogs(storyChoices);
    let endingDialog = await getPowProducerEndingDialog(storyChoices, company);

    // Add to dialogs list if found
    if (nextEventDialogs) {
        dialogs.push(...nextEventDialogs);
    }
    if (endingDialog) {
        dialogs.push(endingDialog);
    }

    return dialogs;
}

function getPowProducerNextEventDialogs(storyChoices) {
    let dialogs = [];
    for (let storyChoice of storyChoices) {
        let {storyPosition, resultWord} = getPowStoryPositionAndResult(storyChoice);

        // Get reaction dialog at 'next_event' if 'normal' not added
        if (storyPosition === 'next_event') {
            let personName = storyChoice.information;
            let nextStoryPosition = getPowNextStoryPosition(storyPosition);
            let nextStoryChoice = storyChoices.find(x => x.information === personName &&
                x.choice.split('_').slice(1).join('_') === nextStoryPosition);
            if (!nextStoryChoice) {
                // Get dialog
                let dialogEvent = `${personName.toLowerCase().replace(' ', '_')}_${resultWord}`;
                let dialogData = getProducerDialogData(dialogEvent);
                dialogs.push(dialogData);
            }
        }
    }
    return dialogs;
}

async function getPowProducerEndingDialog(storyChoices, company) {
    for (let storyChoice of storyChoices) {
        let {storyPosition} = getPowStoryPositionAndResult(storyChoice);

        // Get dialog if at that story position and not at next
        if (['ending', 'goals', 'pre_premier_event'].includes(storyPosition)) {

            // Get next story position
            let nextStoryPosition = getPowNextStoryPosition(storyPosition);
            let nextStoryChoice = storyChoices.find(x => x.choice.split('_').slice(1).join('_') === nextStoryPosition);
            if (!nextStoryChoice) {

                // Skip 'pre_premier_event' dialog if not at monthly event
                if (storyPosition === 'pre_premier_event') {
                    let latestEvent = await getCompanyLatestEvent(company);
                    if (latestEvent.weeklyShow || latestEvent.name === 'Weekly Show') {
                        return null;
                    }
                }

                return getProducerDialogData(storyChoice.choice);
            }
        }
    }
    return null;
}

function getProducerDialogData(storyPosition) {
    let producerDialogData = powDialog['Hank Hughes'];
    let dialogData = producerDialogData[storyPosition];
    dialogData['information'] = producerDialogData['information'];
    return dialogData;
}

async function processPowPremierEvent(company, event, resultWord) {
    // Add Femme Fatale match with top stars and owner promo to premier event
    const game = await getCompanyGame(company);

    // Add once
    if (!(await getEventMatches(event)).length) {

        // Add femme fatale match if all star goals completed
        if (resultWord === 'success') {
            let mainRoster = await getCompanyRosterByType(company, 'main');
            let rosterSuperstars = await getRosterSuperstars(mainRoster);

            // Get stars
            let superstars = [];
            for (let name of ['Lady Julia', 'Violet', 'Natasha Ali', 'Zora']) {
                let superstar = rosterSuperstars.find(x => x.name === name);
                if (superstar) {
                    superstars.push(superstar);
                }
            }

            if (superstars.length >= 2) {
                // Add match
                const newMatch = Match(event.matchesCount, 'singles');
                Matches.insert(newMatch);
                await addEventMatches(event, [newMatch]);
                event.matchesCount += 1;
                await addMatchParticipants(newMatch, superstars);

                // Add gimmick match
                let newGimmickMatch = generateGimmickMatch();
                GimmickMatches.insert(newGimmickMatch);
                newGimmickMatch.name = 'Femme Fatale';
                await addGimmickMatchMatches(newGimmickMatch, [newMatch]);

                // Add feuds
                let tagTeamsPresent = [];
                for (const s of superstars) {
                    tagTeamsPresent.push(...(await getSuperstarTagTeams(s)).filter(x => !x.temporary));
                }
                tagTeamsPresent = getUniqueObjects(tagTeamsPresent);
                let matchFeuds = await getParticipantFeuds(superstars, tagTeamsPresent);
                await addMatchFeuds(newMatch, matchFeuds);
                for (const feud of matchFeuds) {
                    if (!feud.started && feud.directFeudEvent && !feud.ended) {
                        feud.startMonth = game.monthCount;
                        feud.startYear = game.year;
                        feud.runBlowoff = false;
                    }
                }

                // Add managers
                setManagersForMatchOrSegment(newMatch, superstars, rosterSuperstars);
            }
        }

        // Add owner promo
        let text = {};
        text['start'] = ['Hank Hughes comes out to the ring to welcome the fans to the show.'];
        text['action'] = [`"Ladies and Gentlemen, welcome to ${event.name}, one of our most eagerly anticipated events ever! The ` +
        `amazing talents of our women will be showcased tonight as they offer you incredible action ` +
        `and drama that is sure to have you jumping out of your seats. I am Hank Hughes, and this is: ` +
        `Passion. Of. Wrestling!"`];
        let segmentItem = createCustomSegmentItem('Welcome to POW', 1, text, false, false, false, false, false);
        SegmentItems.insert(segmentItem);

        let newSegment = Segment();
        newSegment.hype = 50;
        Segments.insert(newSegment);
        await addSegmentItemSegments(segmentItem, [newSegment]);
        await addEventSegments(event, [newSegment]);
        newSegment.order = event.matchesCount;
        event.matchesCount += 1;

        // Add producer to segment
        let producerData = powDialog['Hank Hughes']['information'];
        let producer = Superstar(producerData['name'], 800, 46);
        Superstars.insert(producer);
        producer.gender = 'male';
        producer.graphic = JSON.parse(JSON.stringify(producerData['graphic']));
        await addSegmentSuperstars(newSegment, [producer]);
    }
}

function getPowPremierEventResult(numberOfStarsMade, eventRating) {
    let result = 'failure';

    if (numberOfStarsMade >= 4) {
        // Success if all star goals completed
        result = 'success'
    }
    else if (numberOfStarsMade >= 1) {
        // Chance for success if at least 1 star goal completed
        if (eventRating >= 3.5) {
            let chance = numberOfStarsMade * 25;
            if (eventRating >= 4.5) {
                chance += 25
            }
            if (getRandomInt(0, 100) <= chance) {
                result = 'success'
            }
        }
    }

    return result;
}

async function processPowPremierEventSuccess(company) {

    // Add creative points
    company.points += myround(getRandomInt(500, 700), 50);

    // Add to stories completed
    const user = loadUser();
    if (user && !user.storiesCompleted.includes('passion')) {
        user.storiesCompleted.push('passion');
        saveUser(user);
    }

    // Add femme fatale
    const femmeFataleMatch = createFemmeFataleMatch();
    GimmickMatches.insert(femmeFataleMatch);
    await addCompanyGimmickMatches(company, [femmeFataleMatch]);

    return true;
}

async function processPowSuperstarLeavesCompany(superstar, company) {
    // Fail star goal if superstar leaves company
    let goals = await getCompanyObjectives(company);
    let goal = goals.find(x => x.name === `Make ${superstar.name} a Star` && !x.failed);

    if (goal) {
        let goalProperty = (await getObjectiveProperties(goal))[0];
        let goalSuperstar = (await getPropertySuperstars(goalProperty))[0];
        if (goalSuperstar.$loki === superstar.$loki) {
            goal.failed = true;
            goal.completed = false;
            let newCompanyAlert = await createCompanyAlert('goalFailed', goal.name);
            await addCompanyCompanyAlerts(company, [newCompanyAlert]);
        }
    }

    return true;
}

export {createFemmeFataleMatch, getPowDialog, createPowObjectives, processPowStarResult, getPowPostEventDialogs,
    processPowStoryChoices, getPowProducerReactionDialogs, processPowSuperstarLeavesCompany}